"use client";

import { useCallback, useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";

import { CrossCircleIcon } from "@/icon/shared";

import { BadgeProps, ScoreBadgeProps } from "./BadgeProps";
import { scoreBadgeColors } from "./dataset";

export const Badge = ({
  children,
  className,
  onClick,
  onRemove,
  iconClassName,
  ariaLabel = "Badge",
  style,
  ...props
}: BadgeProps) => {
  const handleRemoveClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onRemove?.();
    },
    [onRemove]
  );
  return (
    <div
      aria-label={ariaLabel}
      className={twMerge(
        "flex w-fit select-none items-center justify-center gap-2 whitespace-nowrap rounded-md bg-[#EEEEEE] px-3 py-2 text-sm transition-colors",
        className
      )}
      onClick={onClick}
      style={style}
      {...props}
    >
      {children}
      {onRemove && (
        <div onClick={handleRemoveClick} className="h-min w-min">
          <CrossCircleIcon
            aria-label="CrossCircleIcon"
            className={twMerge(
              "cursor-pointer fill-[hsl(214,6%,60%)] transition-colors hover:fill-[hsl(214,6%,70%)]",
              iconClassName
            )}
          />
        </div>
      )}
    </div>
  );
};

export const ScoreBadge = ({
  score,
  className,
  onClick,
  ...props
}: ScoreBadgeProps) => {
  const [lang, setLang] = useState<"en" | "zh">("en");

  const [basicStyles, hoverColor] = scoreBadgeColors[score]
    ? scoreBadgeColors[score]
    : scoreBadgeColors[0];
  const content =
    score <= 0 || score > 5 ? "-" : lang === "en" ? score : `${score} 分`;

  useEffect(() => {
    if (typeof window === "undefined") return;

    const pathname = window.location.pathname;
    if (pathname.startsWith("/zh")) setLang("zh");
    else setLang("en");
  }, []);

  return (
    <Badge
      {...props}
      className={twMerge(
        "h-5 w-9 rounded-full border text-xs font-normal",
        basicStyles,
        onClick != null && `${hoverColor} cursor-pointer`,
        className
      )}
      onClick={onClick}
    >
      {content}
    </Badge>
  );
};

export const SymbolBadge = ({ ...props }: BadgeProps) => (
  <Badge
    {...props}
    ariaLabel="SymbolBadge"
    className={twMerge(
      "flex h-7 w-[60px] rounded-full bg-[#E0F1FA] text-xs font-medium text-stock-mining-dark",
      props.onClick &&
        "cursor-pointer transition hover:bg-stock-mining-dark hover:text-white",
      props.className
    )}
  />
);
